import PlatformModalLayer from '../utils/PlatformModalLayer';
import PopupCloseButton from '../utils/PopupCloseButton';
import React, { useState, useRef, useLayoutEffect } from 'react'
import { Ripple } from '../utils/PopupRipple.jsx';
import Select from "../utils/Select.jsx";
import { useTranslation } from 'react-i18next';
import './deposit.scss';
import { toast } from "react-toastify";
import { useAccountManagerContext } from '../../contexts/Account-Manager-Context';
import { API_ENDPOINT_MAKE_DEPOSIT, API_ENDPOINT_GET_BANK_DETAILS, API_ENDPOINT_PGPAY_CREATE_PAYMENT } from '../../data/Endpoints-API';
import APIMiddleware from '../../data/api/Api-Middleware';
import { useEffect } from 'react';
import { useAuthContext } from '../../contexts/Auth-Context';
import { useChartContext } from '../../contexts/Chart-Context';
import Spinner from '../spinner/Spinner';
import { formatFilterDate } from '../../utils/format.js';
import { modalSelectStyles } from '../../utils/dropdownOptionStyles.js';
import AppDatePicker, {
    DOB_MIN_YEAR,
    toLocalYmd,
    anchorRectFromEvent,
} from '../common/AppDatePicker.jsx';
import '../main/account_manager/Positions.css';

const DepositModal = () => {
    const { t } = useTranslation();

    const options = [
        { value: "sepa", label: t('modals.sepa') },
        { value: "credit_card", label: t('modals.creditCard') }
    ];
    const customStyle = modalSelectStyles('100%');

    const { setShowDeposit, setShowBank, setBankDetails } = useAccountManagerContext();
    const { user, platFromData } = useAuthContext();
    const { selectedStyle } = useChartContext();

    const [amount, setAmount] = useState('');
    const [selectedOption, setSelectedOption] = useState(options[0]);
    const cardRef = useRef(null);
    const previousHeight = useRef(null);
    const heightAnimation = useRef(null);
    useLayoutEffect(() => {
        const card = cardRef.current;
        const from = previousHeight.current;
        previousHeight.current = null;
        if (!card || from == null || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        heightAnimation.current?.cancel();
        const to = card.getBoundingClientRect().height;
        card.classList.add('is-resizing');
        heightAnimation.current = card.animate(
            [{ height: `${from}px` }, { height: `${to}px` }],
            { duration: 280, easing: 'cubic-bezier(.22, 1, .36, 1)' }
        );
        const animation = heightAnimation.current;
        animation.onfinish = () => card.classList.remove('is-resizing');
        return () => {
            animation.onfinish = null;
            animation.cancel();
            card.classList.remove('is-resizing');
        };
    }, [selectedOption?.value]);
    const [beneficiaryName, setBeneficiaryName] = useState(user?.userName || '');
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // PGPay card + billing fields
    const [cardNumber, setCardNumber] = useState('');
    const [cardExpiryMonth, setCardExpiryMonth] = useState('');
    const [cardExpiryYear, setCardExpiryYear] = useState('');
    const [cardCvvNumber, setCardCvvNumber] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [stateCode, setStateCode] = useState('');
    const [zip, setZip] = useState('');
    const [countryCode, setCountryCode] = useState('');

    // Lock flags for fields that are prefilled from user/context
    const [isNameLocked, setIsNameLocked] = useState(false);
    const [isEmailLocked, setIsEmailLocked] = useState(false);
    const [isPhoneLocked, setIsPhoneLocked] = useState(false);
    const [isAddressLocked, setIsAddressLocked] = useState(false);
    const [isCityLocked, setIsCityLocked] = useState(false);
    const [isStateLocked, setIsStateLocked] = useState(false);
    const [isZipLocked, setIsZipLocked] = useState(false);
    const [isCountryLocked, setIsCountryLocked] = useState(false);

    const getDateOnly = (dateString) => {
        return dateString || '';
    };

    const initialDob = getDateOnly(platFromData[5]?.dateOfBirth) || user?.dateOfBirth || '';
    const [dateOfBirth, setDateOfBirth] = useState(initialDob);
    const [isDobLocked, setIsDobLocked] = useState(!!initialDob);
    const [showDobPicker, setShowDobPicker] = useState(false);
    const [dobAnchorRect, setDobAnchorRect] = useState(null);

    useEffect(() => {
        if (platFromData[5]?.dateOfBirth) {
            const dob = getDateOnly(platFromData[5]?.dateOfBirth);
            setDateOfBirth(dob);
            setIsDobLocked(!!dob);
        }
    }, [platFromData]);
  
    const changehandlefunction = (option) => {
        previousHeight.current = cardRef.current?.getBoundingClientRect().height ?? null;
        setSelectedOption(option);

        // Reset card & billing fields when switching away from credit card
        if (option?.value !== 'credit_card') {
            setCardNumber('');
            setCardExpiryMonth('');
            setCardExpiryYear('');
            setCardCvvNumber('');
            setFirstName('');
            setLastName('');
            setEmail('');
            setPhone('');
            setAddress('');
            setCity('');
            setStateCode('');
            setZip('');
            setCountryCode('');
            return;
        }

        // Prefill billing fields from user/beneficiary when switching to credit card
        const fullName = beneficiaryName || user?.userName || '';
        const parts = fullName.trim().split(' ');
        const fName = parts[0] || '';
        const lName = parts.slice(1).join(' ');

        const nextFirst = firstName || user?.first_name || user?.firstName || fName;
        const nextLast = lastName || user?.last_name || user?.lastName || lName || fName;
        const nextEmail = email || user?.email || '';
        const nextPhone = phone || (user?.phone ? String(user.phone) : '');
        const nextAddress = address || user?.address || '';
        const nextCity = city || user?.city || '';
        const nextState = stateCode || user?.state || '';
        const nextZip = zip || user?.zip || '';
        const nextCountry = countryCode || user?.country || '';

        setFirstName(nextFirst);
        setLastName(nextLast);
        setEmail(nextEmail);
        setPhone(nextPhone);
        setAddress(nextAddress);
        setCity(nextCity);
        setStateCode(nextState);
        setZip(nextZip);
        setCountryCode(nextCountry);

        // Lock fields that were filled from context so user cannot edit them
        if (nextFirst || nextLast) {
            setIsNameLocked(true);
        }
        if (nextEmail) {
            setIsEmailLocked(true);
        }
        if (nextPhone) {
            setIsPhoneLocked(true);
        }
        if (nextAddress) {
            setIsAddressLocked(true);
        }
        if (nextCity) {
            setIsCityLocked(true);
        }
        if (nextState) {
            setIsStateLocked(true);
        }
        if (nextZip) {
            setIsZipLocked(true);
        }
        if (nextCountry) {
            setIsCountryLocked(true);
        }
    }
    const handleSubmit = async () => {

        const errors = [];
        const newFieldErrors = {};

        if (!amount) {
            errors.push(t('modals.errors.amountRequired'));
            newFieldErrors.amount = true;
        } else {
            const numericAmount = Number(amount);
            if (Number.isNaN(numericAmount)) {
                errors.push(t('modals.errors.amountMustBeNumber'));
                newFieldErrors.amount = true;
            } else if (numericAmount < 0.01 || numericAmount > 100000) {
                errors.push(t('modals.errors.amountRange'));
                newFieldErrors.amount = true;
            }
        }

        if (!selectedOption) {
            errors.push(t('modals.errors.transactionTypeRequired'));
            newFieldErrors.transactionType = true;
        }

        // Sender name + DOB only required for non-card deposits
        if (selectedOption?.value !== 'credit_card') {
            if (!beneficiaryName) {
                errors.push(t('modals.errors.beneficiaryNameRequired'));
                newFieldErrors.beneficiaryName = true;
            }
            // Only require dateOfBirth for SEPA, not for credit_card
        if (selectedOption.value !== 'credit_card' && !dateOfBirth) {
                errors.push(t('modals.errors.dateOfBirthRequired'));
                newFieldErrors.dateOfBirth = true;
            }
        }

        // Extra validation only for credit card (PGPay) flow
        if (selectedOption?.value === 'credit_card') {
            if (!firstName) {
                errors.push(t('modals.errors.firstNameRequired'));
                newFieldErrors.firstName = true;
            } else if (firstName.length > 100) {
                errors.push(t('modals.errors.firstNameMaxLength'));
                newFieldErrors.firstName = true;
            }

            if (!lastName) {
                errors.push(t('modals.errors.lastNameRequired'));
                newFieldErrors.lastName = true;
            } else if (lastName.length > 100) {
                errors.push(t('modals.errors.lastNameMaxLength'));
                newFieldErrors.lastName = true;
            }

            if (!email) {
                errors.push(t('modals.errors.emailRequired'));
                newFieldErrors.email = true;
            } else {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                    errors.push(t('modals.errors.emailInvalid'));
                    newFieldErrors.email = true;
                } else if (email.length > 255) {
                    errors.push(t('modals.errors.emailMaxLength'));
                    newFieldErrors.email = true;
                }
            }

            if (!phone) {
                errors.push(t('modals.errors.phoneRequired'));
                newFieldErrors.phone = true;
            } else if (phone.length < 10 || phone.length > 15) {
                // phone state already digits only, also <= 30 chars via maxLength
                errors.push(t('modals.errors.phoneLength'));
                newFieldErrors.phone = true;
            }

            if (!address) {
                errors.push(t('modals.errors.addressRequired'));
                newFieldErrors.address = true;
            } else if (address.length > 50) {
                errors.push(t('modals.errors.addressMaxLength'));
                newFieldErrors.address = true;
            }

            if (!city) {
                errors.push(t('modals.errors.cityRequired'));
                newFieldErrors.city = true;
            } else if (city.length > 100) {
                errors.push(t('modals.errors.cityMaxLength'));
                newFieldErrors.city = true;
            }

            if (!stateCode) {
                errors.push(t('modals.errors.stateCodeRequired'));
                newFieldErrors.stateCode = true;
            } else if (stateCode.length < 2 || stateCode.length > 3) {
                errors.push(t('modals.errors.stateCodeLength'));
                newFieldErrors.stateCode = true;
            }

            if (!zip) {
                errors.push(t('modals.errors.zipRequired'));
                newFieldErrors.zip = true;
            } else if (zip.length > 20) {
                errors.push(t('modals.errors.zipMaxLength'));
                newFieldErrors.zip = true;
            }

            if (!countryCode) {
                errors.push(t('modals.errors.countryCodeRequired'));
                newFieldErrors.countryCode = true;
            } else {
                const countryRegex = /^[A-Za-z]{2}$/;
                if (!countryRegex.test(countryCode)) {
                    errors.push(t('modals.errors.countryCodeFormat'));
                    newFieldErrors.countryCode = true;
                }
            }

            const cleanCardNumber = (cardNumber || '').replace(/\s+/g, '');
            if (!cleanCardNumber) {
                errors.push(t('modals.errors.cardNumberRequired'));
                newFieldErrors.cardNumber = true;
            } else if (cleanCardNumber.length < 12 || cleanCardNumber.length > 19) {
                errors.push(t('modals.errors.cardNumberLength'));
                newFieldErrors.cardNumber = true;
            }

            if (!cardExpiryMonth || !cardExpiryYear) {
                errors.push(t('modals.errors.cardExpiryRequired'));
                newFieldErrors.cardExpiryMonth = true;
                newFieldErrors.cardExpiryYear = true;
            } else {
                const monthNum = Number(cardExpiryMonth);
                const yearNum = Number(cardExpiryYear);
                if (
                    Number.isNaN(monthNum) ||
                    monthNum < 1 ||
                    monthNum > 12
                ) {
                    errors.push(t('modals.errors.cardExpiryMonthRange'));
                    newFieldErrors.cardExpiryMonth = true;
                }
                if (Number.isNaN(yearNum) || cardExpiryYear.length !== 4) {
                    errors.push(t('modals.errors.cardExpiryYearFormat'));
                    newFieldErrors.cardExpiryYear = true;
                } else {
                    // Check card not expired
                    const now = new Date();
                    const currentYear = now.getFullYear();
                    const currentMonth = now.getMonth() + 1; // 1-12
                    if (
                        yearNum < currentYear ||
                        (yearNum === currentYear && monthNum < currentMonth)
                    ) {
                        errors.push(t('modals.errors.cardExpired'));
                        newFieldErrors.cardExpiryMonth = true;
                        newFieldErrors.cardExpiryYear = true;
                    }
                }
            }

            if (!cardCvvNumber) {
                errors.push(t('modals.errors.cardCvvRequired'));
                newFieldErrors.cardCvvNumber = true;
            } else if (cardCvvNumber.length < 3 || cardCvvNumber.length > 4) {
                errors.push(t('modals.errors.cardCvvLength'));
                newFieldErrors.cardCvvNumber = true;
            }
        }

        if (errors.length > 0) {
            setFieldErrors(newFieldErrors);

            // For credit card, only highlight fields (no multiple toasts)
            if (selectedOption?.value !== 'credit_card') {
                errors.forEach((error) => {
                    toast.error(error, {
                        position: "top-right",
                    });
                });
            }
            return;
        }

        try {
            setLoading(true);

            // Existing SEPA / non-card flow
            if (selectedOption.value !== 'credit_card') {
                let payload = {
                    amount: amount,
                    payment_method: selectedOption.value,
                    user_detail_id: user.userId,
                    userName: user.userName,
                    beneficiaryName: beneficiaryName,
                };
                // Only include dateOfBirth for SEPA, not for credit_card

                if (selectedOption.value !== 'credit_card' && dateOfBirth) {
                    payload.dateOfBirth = dateOfBirth;
                }

                const response = await APIMiddleware.post(API_ENDPOINT_MAKE_DEPOSIT(), payload);

                // Check if deposit request was successfully submitted
                if (response?.data && (response.data.message || response.data.deposit)) {
                    // Lock DOB after successful submission
                    setIsDobLocked(true);

                    toast.success(response.data.message || t('modals.depositSubmitted'), {
                        position: "top-right",
                    });

                    // Close deposit modal first
                    setShowDeposit(false);

                    // Fetch bank details only if deposit request was successful
                    if(selectedOption.value === 'sepa') {
                        try {
                            const bankDetailsResponse = await APIMiddleware.get(API_ENDPOINT_GET_BANK_DETAILS());
                            
                            // Show popup only if deposit was successful AND bank details exist
                            if (bankDetailsResponse?.bank_details && bankDetailsResponse.bank_details.description) {
                                setBankDetails(bankDetailsResponse.bank_details);
                                setShowBank(true);
                            }
                        } catch (bankError) {
                            console.log('Bank details not available or error fetching:', bankError);
                        }
                    }
                } else {
                    // Deposit submission failed or response is invalid
                    toast.error(t('modals.depositSubmitFailed'), {
                        position: "top-right",
                    });
                }
                return;
            }

            // CREDIT CARD → PGPay (Node/Laravel backend) via API endpoint that uses localStorage.serverUrl
            const clientBaseUrl = window.location.origin;

            const pgpayPayload = {
                amount: Number(amount),
                currency_from: 'EUR',
                first_name: firstName,
                last_name: lastName,
                email,
                phone,
                address,
                city,
                state: stateCode,
                zip,
                country: countryCode,
                cardNumber: cardNumber.replace(/\s+/g, ''),
                cardExpiryMonth,
                cardExpiryYear,
                cardCvvNumber,
                success_redirect_url: `${clientBaseUrl}/deposit/result`,
                fail_redirect_url: `${clientBaseUrl}/deposit/result`,
            };

            const response = await APIMiddleware.post(API_ENDPOINT_PGPAY_CREATE_PAYMENT(), pgpayPayload);

            if (response?.status === '3ds' && response?.['3dsUrl']) {
                window.location.href = response['3dsUrl'];
                return;
            }

            if (response?.status === 'success') {
                toast.success(response?.message || t('modals.depositSuccess'), {
                    position: "top-right",
                });
                setShowDeposit(false);
            } else {
                toast.error(response?.message || t('modals.depositFailed'), {
                    position: "top-right",
                });
            }
        } catch (error) {

            if (error?.response?.data?.errors) {
                const allErrorMessages = Object.keys(error.response.data.errors)
                    .filter(key => Array.isArray(error.response.data.errors[key]))
                    .map(key => error.response.data.errors[key].join(' '))
                    .join('\n');

                if (allErrorMessages) {
                    toast.error(allErrorMessages, {
                        position: "top-right",
                    });
                } else {
                    toast.error(error?.response?.data?.errors, {
                        position: "top-right",
                    });
                }
            } else if (error?.response?.data?.message) {
                toast.error(error.response.data.message, {
                    position: "top-right",
                });
            } else {
                toast.error(t('auth.unknownError'), {
                    position: "top-right",
                });
            }
        }
        finally {
            setLoading(false);
        }
    };
    return (
        <>
        <PlatformModalLayer className="confirm-box-1 deposit-dialog" role="dialog" aria-modal="true" aria-labelledby="deposit-dialog-title">
            <div ref={cardRef} className={`card ${selectedOption?.value === 'credit_card' ? 'card-wide' : ''}`}>
                <div className="model-header deposit-dialog-header">
                <h2 id="deposit-dialog-title">
                    {t('modals.depositTitle')}
                </h2>
                <PopupCloseButton onClick={() => setShowDeposit(false)} />
                </div>

                <div className="popup-form-body deposit-form-body">
                <div className='input-div-tight' >
                    <div>
                        <div className='mt-5 mb-5'>
                            <label className='text-color deposit-label'>{t('modals.enterAmount')}</label>
                        </div>
                        <input
                            className={`amountInput ${fieldErrors.amount ? 'error-border' : ''}`}
                            type="number"
                            onChange={(e) => {
                                setAmount(e.target.value);
                                if (fieldErrors.amount) {
                                    setFieldErrors(prev => ({ ...prev, amount: false }));
                                }
                            }}
                            placeholder={t('modals.enterAmountPlaceholder')}
                            value={amount}
                        />
                    </div>
                    <div>
                        <div className='mt-5 mb-5'>
                            <label className='text-color deposit-label'>{t('modals.selectTransactionType')}</label>
                        </div>
                        <Select
                            classNamePrefix="modal-select"
                            options={options}
                            value={selectedOption}
                            onChange={changehandlefunction}
                            styles={customStyle}
                            isSearchable={false}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            menuShouldScrollIntoView={false}
                        />
                    </div>
                </div>

                {selectedOption?.value !== 'credit_card' && (
                    <>
                        <div className='mx-35 mt-15-custom'>
                            <div className='mt-5 mb-5'>
                                <label className='text-color deposit-label'>{t('modals.senderName')}</label>
                            </div>
                            <input
                                className={`amountInput ${fieldErrors.beneficiaryName ? 'error-border' : ''}`}
                                type="text"
                                onChange={(e) => {
                                    const validValue = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                                    setBeneficiaryName(validValue);
                                    if (fieldErrors.beneficiaryName) {
                                        setFieldErrors(prev => ({ ...prev, beneficiaryName: false }));
                                    }
                                }}
                                placeholder={user?.userName}
                                value={beneficiaryName || user?.userName}
                            />
                        </div>
                        <div className='mx-35 my-15'>
                            <div className='mt-5 mb-5'>
                                <label className='text-color deposit-label'>{t('modals.dateOfBirthRequired')}</label>
                            </div>
                            <input
                                className={`amountInput dob-date-input ${fieldErrors.dateOfBirth ? 'error-border' : ''}${showDobPicker ? ' is-active' : ''}`}
                                type="text"
                                readOnly
                                inputMode="none"
                                autoComplete="off"
                                placeholder={t('modals.selectDateOfBirth')}
                                value={dateOfBirth ? formatFilterDate(dateOfBirth) : ''}
                                disabled={isDobLocked}
                                onClick={(e) => {
                                    if (isDobLocked) return;
                                    e.preventDefault();
                                    setDobAnchorRect(anchorRectFromEvent(e));
                                    setShowDobPicker(true);
                                    if (fieldErrors.dateOfBirth) {
                                        setFieldErrors((prev) => ({ ...prev, dateOfBirth: false }));
                                    }
                                }}
                                onKeyDown={(e) => e.preventDefault()}
                            />
                        </div>
                    </>
                )}

                {selectedOption?.value === 'credit_card' && (
                    <>
                        {/* Row 1: First name, Last name (2 columns) */}
                        <div className='input-div-tight'>
                            <div>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.firstNameRequired')}</label>
                                </div>
                                <input
                                    className={`amountInput ${fieldErrors.firstName ? 'error-border' : ''}`}
                                    type="text"
                                    placeholder={t('modals.firstName')}
                                    maxLength={100}
                                    value={firstName}
                                    onChange={(e) => {
                                        if (!isNameLocked) {
                                            setFirstName(e.target.value);
                                        }
                                        if (fieldErrors.firstName) {
                                            setFieldErrors(prev => ({ ...prev, firstName: false }));
                                        }
                                    }}
                                    disabled={isNameLocked}
                                />
                            </div>
                            <div>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.lastNameRequired')}</label>
                                </div>
                                <input
                                    className={`amountInput ${fieldErrors.lastName ? 'error-border' : ''}`}
                                    type="text"
                                    placeholder={t('modals.lastName')}
                                    maxLength={100}
                                    value={lastName}
                                    onChange={(e) => {
                                        if (!isNameLocked) {
                                            setLastName(e.target.value);
                                        }
                                        if (fieldErrors.lastName) {
                                            setFieldErrors(prev => ({ ...prev, lastName: false }));
                                        }
                                    }}
                                    disabled={isNameLocked}
                                />
                            </div>
                        </div>

                        {/* Row 2: Email, Phone (2 columns) */}
                        <div className='input-div-tight'>
                            <div>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.emailRequired')}</label>
                                </div>
                                <input
                                    className={`amountInput ${fieldErrors.email ? 'error-border' : ''}`}
                                    type="email"
                                    placeholder={t('modals.email')}
                                    maxLength={255}
                                    value={email}
                                    onChange={(e) => {
                                        if (!isEmailLocked) {
                                            setEmail(e.target.value);
                                        }
                                        if (fieldErrors.email) {
                                            setFieldErrors(prev => ({ ...prev, email: false }));
                                        }
                                    }}
                                    disabled={isEmailLocked}
                                />
                            </div>
                            <div>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.phoneRequired')}</label>
                                </div>
                                <input
                                    className={`amountInput ${fieldErrors.phone ? 'error-border' : ''}`}
                                    type="text"
                                    placeholder={t('modals.phoneExample')}
                                    maxLength={30}
                                    value={phone}
                                    onChange={(e) => {
                                        if (!isPhoneLocked) {
                                            const onlyDigits = e.target.value.replace(/\D/g, '');
                                            setPhone(onlyDigits);
                                        }
                                        if (fieldErrors.phone) {
                                            setFieldErrors(prev => ({ ...prev, phone: false }));
                                        }
                                    }}
                                    disabled={isPhoneLocked}
                                />
                            </div>
                        </div>

                        {/* Row 3: Address (full row, with same side spacing as popup) */}
                        <div className='mx-35'>
                            <div className='input-div-tight'>
                                <div style={{ width: '100%' }}>
                                    <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.addressRequired')}</label>
                                    </div>
                                    <input
                                        className={`amountInput ${fieldErrors.address ? 'error-border' : ''}`}
                                        type="text"
                                        placeholder={t('modals.address')}
                                        maxLength={50}
                                        value={address}
                                        onChange={(e) => {
                                            if (!isAddressLocked) {
                                                setAddress(e.target.value);
                                            }
                                            if (fieldErrors.address) {
                                                setFieldErrors(prev => ({ ...prev, address: false }));
                                            }
                                        }}
                                        disabled={isAddressLocked}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Row 4: City, State code */}
                        <div className='input-div-tight'>
                            <div>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.cityRequired')}</label>
                                </div>
                                <input
                                    className={`amountInput ${fieldErrors.city ? 'error-border' : ''}`}
                                    type="text"
                                    placeholder={t('modals.city')}
                                    maxLength={100}
                                    value={city}
                                    onChange={(e) => {
                                        if (!isCityLocked) {
                                            setCity(e.target.value);
                                        }
                                        if (fieldErrors.city) {
                                            setFieldErrors(prev => ({ ...prev, city: false }));
                                        }
                                    }}
                                    disabled={isCityLocked}
                                />
                            </div>
                            <div>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.stateCodeRequired')}</label>
                                </div>
                                <input
                                    className={`amountInput ${fieldErrors.stateCode ? 'error-border' : ''}`}
                                    type="text"
                                    placeholder={t('modals.stateExample')}
                                    maxLength={3}
                                    value={stateCode}
                                    onChange={(e) => {
                                        if (!isStateLocked) {
                                            setStateCode(e.target.value);
                                        }
                                        if (fieldErrors.stateCode) {
                                            setFieldErrors(prev => ({ ...prev, stateCode: false }));
                                        }
                                    }}
                                    disabled={isStateLocked}
                                />
                            </div>
                        </div>

                        {/* Row 5: ZIP, Country code */}
                        <div className='input-div-tight'>
                            <div>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.zipRequired')}</label>
                                </div>
                                <input
                                    className={`amountInput ${fieldErrors.zip ? 'error-border' : ''}`}
                                    type="text"
                                    placeholder={t('modals.zip')}
                                    maxLength={20}
                                    value={zip}
                                    onChange={(e) => {
                                        if (!isZipLocked) {
                                            setZip(e.target.value);
                                        }
                                        if (fieldErrors.zip) {
                                            setFieldErrors(prev => ({ ...prev, zip: false }));
                                        }
                                    }}
                                    disabled={isZipLocked}
                                />
                            </div>
                            <div>
                                <div className='mt-5 mb-5'>
                                    <label className='text-color deposit-label'>{t('modals.countryCodeRequired')}</label>
                                </div>
                                <input
                                    className={`amountInput ${fieldErrors.countryCode ? 'error-border' : ''}`}
                                    type="text"
                                    placeholder={t('modals.countryExample')}
                                    maxLength={2}
                                    value={countryCode}
                                    onChange={(e) => {
                                        if (!isCountryLocked) {
                                            setCountryCode(e.target.value.toUpperCase());
                                        }
                                        if (fieldErrors.countryCode) {
                                            setFieldErrors(prev => ({ ...prev, countryCode: false }));
                                        }
                                    }}
                                    disabled={isCountryLocked}
                                />
                            </div>
                        </div>

                        {/* Card details */}
                        <div className='mx-35'>
                            {/* Row 6: Card Number (same width as Address - full row) */}
                            <div className='input-div-tight'>
                                <div style={{ width: '100%' }}>
                                    <div className='mt-5 mb-5'>
                                        <label className='text-color deposit-label'>{t('modals.cardNumberRequired')}</label>
                                    </div>
                                    <input
                                        className={`amountInput ${fieldErrors.cardNumber ? 'error-border' : ''}`}
                                        type="text"
                                        placeholder={t('modals.cardNumberRequired').replace(' *', '')}
                                        maxLength={16}
                                        value={cardNumber}
                                        onChange={(e) => {
                                            const raw = e.target.value.replace(/\D/g, '');
                                            setCardNumber(raw);
                                            if (fieldErrors.cardNumber) {
                                                setFieldErrors(prev => ({ ...prev, cardNumber: false }));
                                            }
                                        }}
                                        style={{
                                            width: '100%',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Row 7: MM, YYYY, CVV aligned in a single row */}
                            <div className='input-div-tight' style={{ gap: '10px', justifyContent: 'flex-start' }}>
                                <div>
                                    <div className='mt-5 mb-5'>
                                        <label className='text-color deposit-label'>{t('modals.expiryMonthLabel')}</label>
                                    </div>
                                    <input
                                        className={`amountInput ${fieldErrors.cardExpiryMonth ? 'error-border' : ''}`}
                                        type="text"
                                        placeholder={t('modals.expiryMonth')}
                                        maxLength={2}
                                        value={cardExpiryMonth}
                                        onChange={(e) => {
                                            const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                                            setCardExpiryMonth(v);
                                            if (fieldErrors.cardExpiryMonth) {
                                                setFieldErrors(prev => ({ ...prev, cardExpiryMonth: false }));
                                            }
                                        }}
                                    />
                                </div>
                                <div>
                                    <div className='mt-5 mb-5'>
                                        <label className='text-color deposit-label'>{t('modals.expiryYearLabel')}</label>
                                    </div>
                                    <input
                                        className={`amountInput ${fieldErrors.cardExpiryYear ? 'error-border' : ''}`}
                                        type="text"
                                        placeholder={t('modals.expiryYear')}
                                        maxLength={4}
                                        value={cardExpiryYear}
                                        onChange={(e) => {
                                            const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                            setCardExpiryYear(v);
                                            if (fieldErrors.cardExpiryYear) {
                                                setFieldErrors(prev => ({ ...prev, cardExpiryYear: false }));
                                            }
                                        }}
                                    />
                                </div>
                                <div>
                                    <div className='mt-5 mb-5'>
                                        <label className='text-color deposit-label'>{t('modals.cvvRequired')}</label>
                                    </div>
                                    <input
                                        className={`amountInput ${fieldErrors.cardCvvNumber ? 'error-border' : ''}`}
                                        type="text"
                                        placeholder={t('modals.cvvRequired').replace(' *', '')}
                                        maxLength={4}
                                        value={cardCvvNumber}
                                        onChange={(e) => {
                                            const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                            setCardCvvNumber(v);
                                            if (fieldErrors.cardCvvNumber) {
                                                setFieldErrors(prev => ({ ...prev, cardCvvNumber: false }));
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
                </div>
                <div className="row-scss">
                    <button
                        onClick={() => setShowDeposit(false)}
                        style={{
                            position: 'relative',
                            overflow: 'hidden',
                            isolation: 'isolate',
                            backgroundColor: selectedStyle.sellColor,
                        }}
                    >
                        <Ripple />
                        {t('modals.cancel')}
                    </button>
                    <div className="blank"></div>
                     <button
                        onClick={handleSubmit}
                        disabled={loading}
                        style={{
                            position: "relative",
                            overflow: "hidden",
                            isolation: "isolate",
                            backgroundColor: selectedStyle.buyColor,
                        }}
                    >
                        <Ripple />
                        {loading ? <Spinner /> : t('modals.submit')}
                    </button>
                </div>
            </div>
        </PlatformModalLayer>
        {showDobPicker && (
            <AppDatePicker
                open
                title="Date of Birth"
                value={dateOfBirth || new Date(new Date().getFullYear() - 18, 0, 1)}
                minYear={DOB_MIN_YEAR}
                overlayZIndex={1000000000001}
                anchorRect={dobAnchorRect}
                buyColor={selectedStyle?.buyColor}
                sellColor={selectedStyle?.sellColor}
                onCancel={() => setShowDobPicker(false)}
                onConfirm={(date) => {
                    setDateOfBirth(toLocalYmd(date));
                    setShowDobPicker(false);
                    if (fieldErrors.dateOfBirth) {
                        setFieldErrors((prev) => ({ ...prev, dateOfBirth: false }));
                    }
                }}
            />
        )}
        </>
    )
}

export default DepositModal
